import { type ReactNode } from 'react'
import { Info, AlertTriangle, Loader2, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * InfoPanel Component
 * Reusable callout panel for informational or warning messages.
 *
 * Variants:
 * - info: Accent-colored panel with border (default)
 * - warning: Inline amber text
 *
 * Modes:
 * - Simple (no title): icon + children as paragraph
 * - Rich (title set): icon + title + children underneath
 *
 * Busy: a spinner in place of the icon and the text dimmed, while what the
 * panel says is being worked out again
 */

export type InfoPanelVariant = 'info' | 'warning'
export type InfoPanelSize = 'default' | 'sm'

export interface InfoPanelProps {
  /** Visual variant (default: 'info') */
  variant?: InfoPanelVariant
  /** Text size (default: 'default'). Use 'sm' for compact contexts like wizards. */
  size?: InfoPanelSize
  /** When set, renders rich mode with bold title + children underneath */
  title?: string
  /** Override the default icon per variant */
  icon?: LucideIcon
  /** A spinner in place of the icon, the text dimmed: its content is being refreshed */
  busy?: boolean
  children: ReactNode
  className?: string
}

const defaultIcons: Record<InfoPanelVariant, LucideIcon> = {
  info: Info,
  warning: AlertTriangle,
}

export function InfoPanel({
  variant = 'info',
  size = 'default',
  title,
  icon,
  busy = false,
  children,
  className,
}: InfoPanelProps) {
  const Icon = busy ? Loader2 : icon ?? defaultIcons[variant]

  const isWarning = variant === 'warning'
  const isSmall = size === 'sm'

  return (
    <div
      aria-busy={busy || undefined}
      className={cn(
        'flex items-start rounded-lg border',
        isSmall ? 'gap-2 p-2.5' : 'gap-2.5 p-3',
        isWarning
          ? 'bg-amber-50 border-amber-200'
          : 'bg-accent/5 border-accent/20',
        className
      )}
    >
      <Icon
        className={cn(
          'mt-0.5 flex-shrink-0',
          isSmall ? 'w-3.5 h-3.5' : 'w-4 h-4',
          isWarning ? 'text-amber-600' : 'text-accent',
          busy && 'motion-safe:animate-spin',
        )}
      />
      {title ? (
        <div className={cn('transition-opacity duration-200', busy && 'opacity-60')}>
          <p className={cn('font-medium', isSmall ? 'text-caption' : 'text-body-sm', isWarning ? 'text-amber-800' : 'text-gray-700')}>{title}</p>
          <div className={cn('text-caption mt-0.5', isWarning ? 'text-amber-700' : 'text-gray-500')}>{children}</div>
        </div>
      ) : (
        <p className={cn('transition-opacity duration-200', busy && 'opacity-60', isSmall ? 'text-caption' : 'text-body-sm', isWarning ? 'text-amber-800' : 'text-accent')}>{children}</p>
      )}
    </div>
  )
}

InfoPanel.displayName = 'InfoPanel'
